import { CalendarDays, CalendarX2 } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { de, enUS } from "react-day-picker/locale";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/shadcn/popover";
import { Separator } from "@/components/shadcn/separator";
import { Button } from "@/components/shadcn/button";
import { Calendar } from "@/components/shadcn/calendar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/shadcn/tooltip";

interface Props {
  label?: string;
  /** ISO string (inclusive lower bound, start of day). */
  from?: string;
  /** ISO string (inclusive upper bound, end of day). */
  to?: string;
  onChange: (range: { from?: string; to?: string }) => void;
}

function formatBound(value?: string) {
  return value ? dayjs(value).format("DD.MM.YY") : "DD.MM.YY";
}

export function FilterDateRangePicker({ label, from, to, onChange }: Props) {
  const { t, i18n } = useTranslation("filters");
  const calendarLocale = i18n.resolvedLanguage?.startsWith("de") ? de : enUS;
  const selected: DateRange | undefined =
    from || to
      ? { from: from ? dayjs(from).toDate() : undefined, to: to ? dayjs(to).toDate() : undefined }
      : undefined;

  const handleSelect = (range: DateRange | undefined) => {
    onChange({
      from: range?.from ? dayjs(range.from).startOf("day").toISOString() : undefined,
      to: range?.to ? dayjs(range.to).endOf("day").toISOString() : undefined,
    });
  };

  const handleReset = () => onChange({ from: undefined, to: undefined });

  return (
    <Popover>
      <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 gap-2">
            <CalendarDays className="h-4 w-4" />
            <span className="text-xs">{label ?? t('datetime.dateRange')}</span>
            <Separator orientation="vertical" className="h-5" />
            <span className="text-xs font-medium">{formatBound(from)} - {formatBound(to)}</span>
          </Button>
      </PopoverTrigger>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleReset}>
            <CalendarX2 className="h-4 w-4 text-red-400" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {t('dateRange.reset')}
        </TooltipContent>
      </Tooltip>

      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          locale={calendarLocale}
          numberOfMonths={2}
          selected={selected}
          defaultMonth={selected?.from}
          onSelect={handleSelect}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
